import { takeLatest, put, call, all, select, delay } from 'redux-saga/effects';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import forEach from 'lodash/forEach';
import get from 'lodash/get';

import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { initiateCheckout, getOrderDetails } from 'yoda-interfaces/lib/Order/OrderApi';
import { getAccountProfile } from 'yoda-interfaces/lib/Account/AccountApi';
import {
    URL_CHECKOUT_PAYMENT,
    URL_CART_PAGE,
    URL_CHECKOUT_PAGE,
    SHIPPING_GROUP_TITLE_SHIPPING,
} from 'yoda-checkout-components/lib/common/Constants';
import { CHECKOUT } from 'yoda-checkout-components/lib/common/LabelConstants';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import * as COAdjustmentActionTypes from 'yoda-checkout-components/lib/actionTypes/COAdjustmentActionTypes';
import {
    getOrigin,
    getClientId,
    isDiscontinuedItem,
    isOrderEligibleApplePay,
    isPayPalStrategiesAvailable,
    updateUserZipcode,
    getOrderGiftRegistries,
} from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import {
    isOOSItem,
    updateCartUID,
} from 'yoda-checkout-components/lib/helpers/Utils/ATCCommonUtils';
import { doPostSSRLogs, getUserZipCode } from 'yoda-core-components/lib/helpers/Utils/Utils';
import itemActions from 'yoda-site-components/lib/actions/ItemCountAction';
import sessionAction from 'yoda-site-components/lib/actions/SessionAction';
import { SET_SERVICE_ERROR as SITE_COMPONENT_SET_SERVICE_ERROR } from 'yoda-site-components/lib/common/Constants';
import { updateCartCount } from 'yoda-site-components/lib/actions/NativeAppAction';
import { selectContext } from 'yoda-site-components/lib/selectors/ContextSelector';
import { setOrderErrorMessage } from 'yoda-checkout-components/lib/actions/ServiceErrorHandlerAction';
import { setApplePayOrderError } from 'yoda-checkout-components/lib/actions/PayWithAppleAction';
import { CLEAR_ORDER_ERROR } from 'yoda-checkout-components/lib/actionTypes/ServiceErrorHandlerActionTypes';
import User from 'yoda-site-components/lib/helpers/User/User';
import {
    checkException,
    handleAuroraRedirection,
} from 'yoda-checkout-components/lib/helpers/ExceptionHandler';
import { stringifyQuery } from 'yoda-core-components/lib/navigation/query/makeQuery';
import { ANALYTICS_CLICK_EVENT } from 'yoda-checkout-components/lib/actionTypes/CheckoutAnalyticsActionTypes';
import { CART_DETAILS_GET_REQUEST, CART_DETAILS_GET_ERROR } from '../actionTypes/CartActionTypes';
import { GET_PROFILE_INFO } from '../actionTypes/CheckoutActionTypes';
import { LAST_ORDER_GET_REQUEST_SUCCESS } from '../actionTypes/OrderActionTypes';
import { SET_SERVICE_ERROR } from '../actionTypes/ShipAndPayActionTypes';
// eslint-disable-next-line import/no-cycle
import OrderDetailsModel from '../helpers/OrderDetailsModel';
import { getAddressList, getAddressListWithGR } from './ShippingAddressSaga';
import ApplicationLogger from '../helpers/ApplicationLogger';
import {
    changeAccordion,
    validateShipping,
    setProfileInfo,
    initNewCheckoutSectionsClientSideActions,
} from '../actions/CheckoutPageActions';
import ShippingService from '../services/ShippingService';
import { updateShippingAddressFormValue } from '../actions/NewShippingAction';
import { isMaxLengthPaypalServiceError } from '../helpers/Utils/Utils';

const locationServiceCookies = {
    akamaiStore: 'akamaiStore',
    userPreferredStore: 'userPreferredStore',
};

export const coCoupons = (state) => state.coCoupons;
const logger = ApplicationLogger.getLogger();

function checkHeaderException(headers) {
    if (!__SERVER__) {
        const checkoutURLConfig = {
            pathname: URL_CHECKOUT_PAGE,
        };
        const errorCodes = ['COUNTRY_OR_STATE_EMPTY'];
        const xWarning = headers.get('X-WARNING') || '';
        if (xWarning && errorCodes.indexOf(xWarning) !== -1) {
            checkoutURLConfig.search = stringifyQuery({
                modifyAction: SHIPPING_GROUP_TITLE_SHIPPING,
            });
            getBrowserHistory().push(checkoutURLConfig);
        }
    }
}

function* moveAccordion() {
    const checkoutPage = (state) => state.checkoutPage;
    const checkoutState = yield select(checkoutPage);
    const { selectedAccordionIndex } = checkoutState || {};
    const paymentIndex = ((checkoutState || {}).payment || {}).index;
    if (selectedAccordionIndex !== paymentIndex) {
        yield all([put(changeAccordion(paymentIndex)), put(validateShipping(true))]);
    }
}

function* getProfileInfoSaga() {
    const action = {};
    let profileObj = {};
    const userProfileData = {};
    const result = yield call(FactorySaga, getAccountProfile, action);
    if (result.isSuccess) {
        profileObj = ((result || {}).response || {}).data || {};
        if (profileObj.firstName) {
            userProfileData.firstName = profileObj.firstName;
        }
        if (profileObj.lastName) {
            userProfileData.lastName = profileObj.lastName;
        }
        if (profileObj.phoneNumber) {
            userProfileData.phoneNumber = profileObj.phoneNumber;
        }
        yield put(setProfileInfo(userProfileData));
        yield put(updateShippingAddressFormValue('firstName', userProfileData.firstName));
        yield put(updateShippingAddressFormValue('lastName', userProfileData.lastName));
        yield put(updateShippingAddressFormValue('phone', userProfileData.phoneNumber));
    }
}

export default function* cartDetailsSaga(action) {
    const modifyAction = action;
    const context = (state) => state.context;
    const checkoutPage = (state) => state.checkoutPage;
    const orderDetails = (state) => state.orderDetails;
    const orderItems = (state) => state.orderItems;
    const orderItemsData = yield select(orderItems);
    const checkoutState = yield select(checkoutPage);
    const appContext = yield select(context);
    const orderDetailsState = yield select(orderDetails);
    const deviceType = appContext && appContext.deviceType ? appContext.deviceType : '';
    const enableNewCheckoutPage = true;
    const isNative = yield select((state) => get(state, 'context.isNative', false));

    const {
        enableCheckoutServerSideRendering = true,
        enablePreSourcing = true,
        enableSingleDeliveryDateSTH = true,
    } = appContext?.featureFlags || {};
    if (__SERVER__ && !enableCheckoutServerSideRendering) {
        return null;
    }
    let appliedAdjustmentsResponse;
    if (modifyAction.payload && modifyAction.payload.appliedAdjustmentsResponse) {
        appliedAdjustmentsResponse = modifyAction.payload.appliedAdjustmentsResponse;
        delete modifyAction.payload.appliedAdjustmentsResponse;
    }
    const isInitFromApplePay = (modifyAction.payload || {}).initiatedFrom === 'applePay';
    const isInitFromApplyCouponSuccess = modifyAction.initiatedFrom === 'apply_coupon_success';
    // Query param to get the error messages
    // pass status,delivery only for checkout out deliverypage
    modifyAction.payload
        ? (modifyAction.payload.expand = 'status,delivery')
        : (modifyAction.payload = { expand: 'status,delivery' });
    let nextPage = null;
    let initGetDraftOrder = false;
    const { isPreLoadCall } = modifyAction.payload;
    delete modifyAction.payload.isPreLoadCall;
    if (modifyAction.payload.nextPage) {
        nextPage = modifyAction.payload.nextPage;
        delete modifyAction.payload.nextPage;
    }
    const { renderFrom } = modifyAction.payload;
    const { ignoreDAReadyEvent } = modifyAction.payload;
    if (ignoreDAReadyEvent) {
        delete modifyAction.payload.ignoreDAReadyEvent;
    }
    if (renderFrom) {
        delete modifyAction.payload.renderFrom;
        yield put(showGlobalLoader(true));
    }
    const { isLoadedFromReview } = modifyAction.payload;
    delete modifyAction.payload.isLoadedFromReview;
    const xDeleteOOSItemsRequest = modifyAction.payload.xDeleteOOSItems;
    modifyAction.showRefresh = true;
    let logInCheckFromServer = false;
    let cookies = {};
    if (__SERVER__) {
        modifyAction.headers = appContext.headers;
        cookies = modifyAction.headers ? modifyAction.headers.cookies : cookies;
        logInCheckFromServer = ((modifyAction.headers || {}).cookies || {}).DP_USER_STATE === '1';
    }
    let errorHandlerPayload = '';
    if (modifyAction.payload.errorHandlerInfo) {
        errorHandlerPayload = modifyAction.payload.errorHandlerInfo;
        delete modifyAction.payload.errorHandlerInfo;
    }

    let pageName = 'checkout';
    let origin = '';
    if (modifyAction.payload.pageName) {
        if (
            modifyAction.payload.pageName === 'payment' ||
            modifyAction.payload.pageName === 'review' ||
            nextPage === '/checkout/review'
        ) {
            modifyAction.payload.expand = 'status,delivery,billing';
        }
        pageName = get(modifyAction, 'payload.pageName', pageName);
        switch (pageName) {
            case 'payment':
                origin = 'billing';
                break;
            case 'delivery':
                origin = 'shipping';
                break;
            default:
                origin = pageName;
        }
        delete modifyAction.payload.pageName;
    } else {
        origin = getOrigin(checkoutState);
        pageName = origin;
    }
    modifyAction.payload.origin = modifyAction.payload.origin
        ? modifyAction.payload.origin
        : origin;

    if (!modifyAction.payload.includeServiceLevels || enableNewCheckoutPage) {
        modifyAction.payload.includeServiceLevels = true;
        modifyAction.payload.expand = 'status,delivery,billing';
    }

    if (
        (User.isUserLoggedIn() || logInCheckFromServer) &&
        modifyAction.payload.includeServiceLevels
    ) {
        modifyAction.payload.expand = 'status,delivery,billing';
    }

    if (modifyAction.payload.initGetDraftOrder && (User.isUserLoggedIn() || logInCheckFromServer)) {
        initGetDraftOrder = true;
        delete modifyAction.payload.initGetDraftOrder;
    }
    modifyAction.apiName = 'GET_CART_API';
    // to pass all GET order service call to find device type.
    if (!isEmpty(deviceType)) {
        modifyAction.payload.clientId = getClientId(deviceType);
    }
    // pass single delivery date zipcode to header
    if (enableSingleDeliveryDateSTH) {
        const ssrCookies = __SERVER__ && cookies;
        const userZipcode = getUserZipCode(ssrCookies, true, locationServiceCookies);
        if (userZipcode) {
            modifyAction.payload.akZip = userZipcode;
        }
    }
    let result = {};
    if (modifyAction.payload.initiateCheckout) {
        // Temporary workaround to make 2 api calls to get the updated cart data
        const initAction = {
            payload: { xCommand: 'initiate-checkout', isVendorHeaderRequired: true },
        };

        if (xDeleteOOSItemsRequest) {
            // For Aurora => sending oosItems ids instead of just sending some of the items are OOS
            const itemIDs = [];
            forEach(orderItemsData, (item) => {
                if (isOOSItem(item) || isDiscontinuedItem(item)) {
                    itemIDs.push(item.id);
                }
            });
            if (itemIDs) {
                initAction.payload.itemsToBeDeleted = itemIDs;
            }
        } else {
            initAction.payload.xDeleteOOSItems = xDeleteOOSItemsRequest;
        }
        if (enablePreSourcing) {
            const isReviewPage = pageName === 'review';
            const clientSource = isReviewPage ? 'Review' : action.xClientSource;
            initAction.payload.xClientSource = clientSource || CHECKOUT;
        }

        const initResponse = yield call(FactorySaga, initiateCheckout, initAction);
        // TODO handle error case
        if (initResponse.isSuccess) {
            delete modifyAction.payload.initiateCheckout;
            delete modifyAction.payload.xDeleteOOSItems;
            modifyAction.payload.expand = 'status,delivery,billing';
            result = yield call(FactorySaga, getOrderDetails, modifyAction, logger);
            if (enableNewCheckoutPage && result.isSuccess && xDeleteOOSItemsRequest) {
                const shippingAddress = ShippingService.getOrderShippingAddress(result.response);
                if (isEmpty(shippingAddress)) {
                    const bopisAddress = ShippingService.getOrderBOPISInfo(result.response);
                    if (isEmpty(bopisAddress)) {
                        window.location.href = URL_CART_PAGE;
                    } else {
                        yield put(initNewCheckoutSectionsClientSideActions(URL_CHECKOUT_PAYMENT));
                    }
                    yield put(showGlobalLoader(false));
                    return null;
                }
            }
        } else if (initResponse.response.status === 500) {
            yield put({
                type: SET_SERVICE_ERROR,
                errorHandlerInfo: {
                    errorCode: 'GENERIC_API_ERROR',
                    showGenericError: true,
                    context: 'GENERIC_API_ERROR',
                    showRefresh: true,
                },
            });
        }
    } else {
        if (enablePreSourcing) {
            modifyAction.payload.xClientSource =
                modifyAction.payload.origin === 'review'
                    ? 'Review'
                    : modifyAction.xClientSource || CHECKOUT;
        }
        result = yield call(FactorySaga, getOrderDetails, modifyAction, logger);
    }
    if (!enableNewCheckoutPage || !renderFrom) {
        yield put(showGlobalLoader(false));
    }
    if (result.isSuccess) {
        const cartDetailsResponse = result.response;
        cartDetailsResponse.applePayEligible = isOrderEligibleApplePay(
            appContext,
            cartDetailsResponse
        );
        cartDetailsResponse.isPayPalStrategiesAvailable = isPayPalStrategiesAvailable(
            cartDetailsResponse
        );
        cartDetailsResponse.renderFrom = renderFrom;

        if (enableNewCheckoutPage && !renderFrom && ['shipping', 'billing'].includes(origin)) {
            cartDetailsResponse.renderFrom = orderDetailsState.renderFrom;
        }

        let bagItemCount = cartDetailsResponse.headers.get('X-Item-Count');
        if (isNil(bagItemCount)) {
            // If the header is empty then check in the response for bag item count.
            bagItemCount =
                (cartDetailsResponse.data.bag && cartDetailsResponse.data.bag.itemCount) || 0;
        }
        yield put(itemActions.updateItemCountAction(bagItemCount));
        if (isNative) {
            const oldBagItemCount = get(orderDetailsState, 'data.bag.itemCount', 0);
            const latestBagItemCount = get(cartDetailsResponse, 'data.bag.itemCount', 0);
            if (!isEqual(oldBagItemCount, latestBagItemCount)) {
                yield put(updateCartCount(bagItemCount));
            }
        }
        const orderDetailsModel = new OrderDetailsModel(cartDetailsResponse);
        if (User.isUserLoggedIn() || logInCheckFromServer) {
            yield put(orderDetailsModel.getExpressCheckoutInfo(true));
        }
        yield put(orderDetailsModel.checkInternationalShippingError());
        yield put({ type: LAST_ORDER_GET_REQUEST_SUCCESS, orderDetails: cartDetailsResponse });
        let actioName = '';
        if (isEmpty(orderDetailsState) && modifyAction.payload.modifyAction) {
            actioName = modifyAction.payload.modifyAction;
        }
        if (appliedAdjustmentsResponse) {
            appliedAdjustmentsResponse.adjustments = get(
                cartDetailsResponse,
                'data.adjustments',
                []
            );
            yield put({
                type: COAdjustmentActionTypes.CO_ADJUSTMENTS_BY_ID_DELETE_SUCCESS,
                appliedAdjustmentsResponse,
            });
        }
        if (isInitFromApplyCouponSuccess) {
            yield put({
                type: COAdjustmentActionTypes.CO_ADJUSTMENTS_POST_SUCCESS_REQUEST,
                callbackValue: {
                    ...modifyAction.callbackValue,
                    orderDetails: result,
                    orderDetailsState,
                },
            });
            /**
             * Instead of clearing all the SRVErr
             *  need to clear that is needed
             */
            const isPaypalSRVErr = isMaxLengthPaypalServiceError(cartDetailsResponse);
            isEmpty(isPaypalSRVErr)
                ? yield put({ type: CLEAR_ORDER_ERROR, payload: 'reviewPayment' })
                : '';
        }
        /**
         * To add or update cookie 'DP_USER_DEFAULT_ADDRESS_ZIP' value based on shipping address zipcode.
         * To Add or Update UID Cookie
         */
        if (!__SERVER__) {
            enableSingleDeliveryDateSTH && updateUserZipcode(cartDetailsResponse);
            updateCartUID(cartDetailsResponse);
        }
        yield all([
            put(orderDetailsModel.getOrderInfo(nextPage, appContext, isPreLoadCall)),
            put(orderDetailsModel.getOrderTotals()),
            put(orderDetailsModel.getOrderAdjustments()),
            put(orderDetailsModel.getAppliedRewards()),
            put(orderDetailsModel.getOrderItems()),
            put(orderDetailsModel.getBillingInfo()),
            put(orderDetailsModel.getContactInfo()),
            put(orderDetailsModel.getGiftWrapInfo()),
            put(orderDetailsModel.getShippingGroupInfo()),
            put(orderDetailsModel.getItemDeliverySummary()),
            put(orderDetailsModel.getOrderCharges()),
            put(orderDetailsModel.getOrderPaymentInfo()),
            put(orderDetailsModel.getCheckoutAccordionSections(actioName)),
            put(orderDetailsModel.validateShippingAddress()),
            put(orderDetailsModel.checkErrors()),
            put(orderDetailsModel.getShippingMethodsList()),
            put(orderDetailsModel.getAlternateCharges()),
        ]);
        if (!modifyAction.adjustmentApplied) {
            yield put(orderDetailsModel.getCreditCardInfo());
        }
        // On OOS Item removal from Popup update checkoutInfo Reducer(SDPU Section)
        const isDeleteFromOOS = get(modifyAction, 'payload.isDeleteFromOOS', false);
        if (enableNewCheckoutPage && (renderFrom || isDeleteFromOOS)) {
            const initNewCheckoutSections = orderDetailsModel.initNewCheckoutSections(
                put,
                renderFrom,
                isLoadedFromReview,
                ignoreDAReadyEvent,
                isDeleteFromOOS
            );
            const { initCheckoutActions } = initNewCheckoutSections;
            const { initPageRenderActions } = initNewCheckoutSections;
            yield all(initCheckoutActions);
            yield call(initPageRenderActions);
        }
        if (
            enableNewCheckoutPage &&
            (isInitFromApplyCouponSuccess || modifyAction.adjustmentApplied)
        ) {
            const checkoutInfoSelector = (state) => state.checkoutInfo;
            const checkoutInfo = yield select(checkoutInfoSelector);
            const updateNewCheckoutActions = orderDetailsModel.updateNewCheckoutSection(
                put,
                checkoutInfo
            );
            yield all(updateNewCheckoutActions);
        }
        if (initGetDraftOrder) {
            const orderGiftRegistries =
                result.response &&
                result.response.data &&
                getOrderGiftRegistries(result.response.data);
            orderGiftRegistries && orderGiftRegistries.itemsPresent
                ? yield call(getAddressListWithGR, modifyAction.headers)
                : yield call(getAddressList, modifyAction.headers);
        }
        if (nextPage) {
            yield put(showGlobalLoader(true));
            getBrowserHistory().push(nextPage);
        }
        if (!__SERVER__ && ((cartDetailsResponse.data || {}).status || {}).exceptions) {
            const isReviewPage = pageName === 'review';
            const enableClickStreamLogs = get(
                appContext.featureFlags,
                'enableClickStreamLogs',
                false
            );
            let orderExceptionInfo = handleAuroraRedirection(
                cartDetailsResponse.data.status.exceptions,
                moveAccordion,
                isReviewPage,
                enableNewCheckoutPage,
                isLoadedFromReview,
                false,
                enableClickStreamLogs
                // pageName
            );
            if (!orderExceptionInfo) {
                orderExceptionInfo = checkException(
                    cartDetailsResponse.data.status.exceptions,
                    isReviewPage,
                    enableNewCheckoutPage,
                    enableClickStreamLogs
                );
            }

            if (!isEmpty(orderExceptionInfo)) {
                yield put(setOrderErrorMessage(orderExceptionInfo));
            }
        }

        if (!__SERVER__ && cartDetailsResponse.headers) {
            setTimeout(() => {
                checkHeaderException(cartDetailsResponse.headers);
            }, 0);
        }
        if (modifyAction.errorHandlerInfo && modifyAction.errorHandlerInfo.isApplePay) {
            yield put({
                type: SITE_COMPONENT_SET_SERVICE_ERROR,
                errorHandlerInfo: modifyAction.errorHandlerInfo,
            });
        }
        if (errorHandlerPayload) {
            yield put({ type: SET_SERVICE_ERROR, errorHandlerInfo: errorHandlerPayload });
        }
        const oosSelector = (state) => state.oosDetails;
        const oosDetails = yield select(oosSelector);
        if (!oosDetails.isShowModal && !get(cartDetailsResponse, 'data.delivery.groups')) {
            yield put(sessionAction.setSessionTimeOut(true));
        }
        if (action.isRewardsSlider) {
            appliedAdjustmentsResponse = {
                adjustments: cartDetailsResponse.data.adjustments,
                lastTouchedAdjustment: {
                    ...cartDetailsResponse.data.adjustments[0],
                    method: 'selected from list',
                },
            };
            yield put({
                type: COAdjustmentActionTypes.CO_ADJUSTMENTS_POST_SUCCESS,
                appliedAdjustmentsResponse,
            });
            yield put({
                type: ANALYTICS_CLICK_EVENT,
                payload: 'COUPON_APPLIED_SUCCESS',
            });
        }
    } else {
        const data = result.response && result.response.data ? result.response.data : null;
        yield put({ type: CART_DETAILS_GET_ERROR, data });
        yield put(showGlobalLoader(false));
    }
    pageName = pageName === 'review' ? pageName : 'checkout';
    if (!isInitFromApplePay) {
        yield put(setApplePayOrderError({}));
    }
    const { cluster } = yield select(selectContext);
    doPostSSRLogs(
        logger,
        {
            requestInfo: `IS ${pageName} PAGE RENDERED ON SERVER SIDE : ${
                enableCheckoutServerSideRendering && result.isSuccess
            }`,
            response: result.response,
        },
        cookies,
        cluster
    );
    return result.response;
}

const watchCartDetails = function* watchCartDetails() {
    const task = yield takeLatest(CART_DETAILS_GET_REQUEST, cartDetailsSaga);
    const context = (state) => state.context;
    const appContext = yield select(context);
    const { maxCartResponseTime = 4000 } = (appContext || {}).featureFlags || {};
    if (__SERVER__ && !isEmpty(maxCartResponseTime) && maxCartResponseTime > 0) {
        yield call(delay, maxCartResponseTime);
        task.cancel();
    }
};
watchCartDetails.sagaName = 'watchCartDetails';

const watchGetProfileInfo = function* watchGetProfileInfo() {
    yield takeLatest(GET_PROFILE_INFO, getProfileInfoSaga);
};
watchGetProfileInfo.sagaName = 'watchGetProfileInfo';

export { watchCartDetails, watchGetProfileInfo };
